import { FORM_ID as SPACE_FORM_ID } from "@circle-react/components/Spaces/NewV3/constants";
import type { SpaceType } from "@circle-react/types/Space";
import { reject } from "lodash";

import { t } from "@/i18n-js/instance";

import {
  COURSE_TYPE_FORM_ID,
  SPACE_TYPE_FORM_ID,
  SPACE_TYPE_OPTIONS,
  STEP,
  type SpaceIconType,
} from "./constants";
import type { Step } from "./hooks/useCreateSpaceState";

export interface PrimaryAction {
  disabled?: boolean;
  form: string;
  label: string;
  loading?: boolean;
}

export interface SpaceTypeOption {
  descriptionKey: string;
  icon: SpaceIconType;
  label: string;
  value: SpaceType;
}

const spaceTitles: Record<SpaceType, () => string> = {
  basic: () => t("spaces.form.create.space_titles.basic"),
  chat: () => t("spaces.form.create.space_titles.chat"),
  course: () => t("spaces.form.create.space_titles.course"),
  event: () => t("spaces.form.create.space_titles.event"),
  image: () => t("spaces.form.create.space_titles.image"),
  members: () => t("spaces.form.create.space_titles.members"),
};

const FORM_ID_BY_STEP = {
  [STEP.type]: SPACE_TYPE_FORM_ID,
  [STEP.course]: COURSE_TYPE_FORM_ID,
} as const;

export const titleForStep = (step: Step): string => {
  if (step.id === STEP.form) {
    return spaceTitles[step.postType]();
  }
  return step.id === STEP.type
    ? t("spaces.form.create.v4.step_titles.choose_space_type")
    : t("spaces.form.create.v4.step_titles.choose_course_type");
};

export const primaryActionForStep = (
  step: Step,
  isSubmitting: boolean,
  isFormDisabled = false,
): PrimaryAction => {
  if (step.id === STEP.form) {
    return {
      label: isSubmitting ? t("creating") : t("create_space"),
      form: SPACE_FORM_ID,
      loading: isSubmitting,
      disabled: isSubmitting || isFormDisabled,
    };
  }
  return {
    label: t("spaces.form.create.space_types.next"),
    form: FORM_ID_BY_STEP[step.id],
  };
};

interface BuildSpaceTypeOptionsParams {
  isCoursesEnabled: boolean;
}

export const buildSpaceTypeOptions = ({
  isCoursesEnabled,
}: BuildSpaceTypeOptionsParams): SpaceTypeOption[] => {
  const visible = isCoursesEnabled
    ? SPACE_TYPE_OPTIONS
    : reject(SPACE_TYPE_OPTIONS, { value: "course" });

  return visible.map(option => ({
    value: option.value,
    icon: option.icon,
    label: option.getLabel(),
    descriptionKey: option.descriptionKey,
  }));
};
